import { Injectable, inject } from '@angular/core';
import {
  HttpClient,
  HttpEvent,
  HttpResponse,
  HttpEventType,
  HttpProgressEvent,
} from '@angular/common/http';
import { Observable } from 'rxjs';
import { scan } from 'rxjs/operators';
import { firstValueFrom } from 'rxjs';
import { tap, filter } from 'rxjs/operators';
import { LP_UI_CONFIG } from '../core/tokens';
import { StorageService } from '../services/storage.service';
import {
  AdditionalDocument,
  EventDocumentation,
  EventAttachment,
  Upload,
} from '../models/documentation.model';
import { BuildingPlan } from '../models/building-plan.model';

@Injectable({ providedIn: 'root' })
export class DocumentationApi {
  private readonly cfg = inject(LP_UI_CONFIG);
  private http = inject(HttpClient);
  private storage = inject(StorageService);

  private get headers() {
    return {
      Authorization: 'Bearer ' + this.storage.token(),
    };
  }

  // ==================== Building Plans ====================

  async uploadBuildingPlan(
    objectId: number,
    fileName: string,
    fileData: string,
    onProgress?: (progress: number) => void
  ): Promise<BuildingPlan> {
    const request$ = this.http
      .post<BuildingPlan>(
        `${this.cfg.host}api/upload_building_plan/`,
        {
          objectID: objectId,
          fileName,
          fileData,
        },
        {
          headers: this.headers,
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        tap((event: HttpEvent<BuildingPlan>) => {
          if (
            event.type === 1 &&
            'total' in event &&
            'loaded' in event &&
            event.total &&
            onProgress
          ) {
            const progress = Math.min(Math.round((100 * event.loaded) / event.total), 98);
            onProgress(progress);
          } else if (event.type === 4 && onProgress) {
            onProgress(100);
          }
        }),
        filter(
          (event: HttpEvent<BuildingPlan>): event is HttpResponse<BuildingPlan> => event.type === 4
        )
      );

    const event = await firstValueFrom(request$);
    return event.body ?? { name: '', storageLink: '' };
  }

  async getBuildingPlan(objectId: number): Promise<BuildingPlan | null> {
    return firstValueFrom(
      this.http.get<BuildingPlan>(`${this.cfg.host}api/bulding_plan/${objectId}`, {
        headers: this.headers,
      })
    );
  }

  async deleteBuildingPlan(objectId: number): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${this.cfg.host}api/delete_building_plan/`,
        { objectId },
        { headers: this.headers }
      )
    );
  }

  // ==================== Additional Documents ====================

  async uploadAdditionalDocument(
    objectId: number,
    sensorId: number,
    fileName: string,
    fileData: string,
    onProgress?: (progress: number) => void
  ): Promise<AdditionalDocument> {
    const request$ = this.http
      .post<AdditionalDocument>(
        `${this.cfg.host}api/upload_additional_document/`,
        {
          objectID: objectId,
          sensorID: sensorId,
          fileName,
          fileData,
        },
        {
          headers: this.headers,
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        tap((event: HttpEvent<AdditionalDocument>) => {
          if (
            event.type === 1 &&
            'total' in event &&
            'loaded' in event &&
            event.total &&
            onProgress
          ) {
            const progress = Math.min(Math.round((100 * event.loaded) / event.total), 98);
            onProgress(progress);
          } else if (event.type === 4 && onProgress) {
            onProgress(100);
          }
        }),
        filter(
          (event: HttpEvent<AdditionalDocument>): event is HttpResponse<AdditionalDocument> =>
            event.type === 4
        )
      );

    const event = await firstValueFrom(request$);
    return event.body ?? ({} as AdditionalDocument);
  }

  async getAdditionalDocuments(sensorId: number): Promise<AdditionalDocument[]> {
    return firstValueFrom(
      this.http.get<AdditionalDocument[]>(`${this.cfg.host}api/additional_document/${sensorId}`, {
        headers: this.headers,
      })
    );
  }

  async renameAdditionalDocument(
    documentId: number,
    name: string,
    onProgress?: (progress: number) => void
  ): Promise<AdditionalDocument> {
    const request$ = this.http
      .post<AdditionalDocument>(
        `${this.cfg.host}api/rename_additional_document/`,
        {
          documentID: documentId,
          name,
        },
        {
          headers: this.headers,
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        tap((event: HttpEvent<AdditionalDocument>) => {
          if (
            event.type === 1 &&
            'total' in event &&
            'loaded' in event &&
            event.total &&
            onProgress
          ) {
            const progress = Math.min(Math.round((100 * event.loaded) / event.total), 98);
            onProgress(progress);
          } else if (event.type === 4 && onProgress) {
            onProgress(100);
          }
        }),
        filter(
          (event: HttpEvent<AdditionalDocument>): event is HttpResponse<AdditionalDocument> =>
            event.type === 4
        )
      );

    const event = await firstValueFrom(request$);
    return event.body ?? ({} as AdditionalDocument);
  }

  async deleteAdditionalDocument(documentId: number): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${this.cfg.host}api/delete_additional_document/`,
        { documentID: documentId },
        { headers: this.headers }
      )
    );
  }

  // ==================== Event Documentation ====================

  /**
   * Get unfinished documentation for an asset or create a new one
   * @param assetId - Asset ID
   * @param sensorId - Optional sensor ID
   * @returns Promise with event documentation
   */
  async getUnfinishedDocumentation(
    assetId: number,
    sensorId?: number
  ): Promise<EventDocumentation> {
    if (!assetId) {
      return {} as EventDocumentation;
    }

    const url =
      sensorId && sensorId > 0
        ? `${this.cfg.host}api/unfinished_documentation/${assetId}/${sensorId}`
        : `${this.cfg.host}api/unfinished_documentation/${assetId}`;

    return firstValueFrom(
      this.http.get<EventDocumentation>(url, {
        headers: this.headers,
      })
    );
  }

  /**
   * Finish documentation and write to event history
   * @param documentationId - Documentation ID to finish
   * @returns Promise with success status
   */
  async finishDocumentation(documentationId: number): Promise<boolean> {
    if (!documentationId) {
      return false;
    }

    return firstValueFrom(
      this.http.get<boolean>(`${this.cfg.host}api/finish_documentation/${documentationId}`, {
        headers: this.headers,
      })
    );
  }

  /**
   * Update event documentation details
   * @param documentation - Documentation object to update
   * @returns Promise with updated documentation
   */
  async updateDocumentation(documentation: EventDocumentation): Promise<EventDocumentation> {
    if (!documentation) {
      return {} as EventDocumentation;
    }

    return firstValueFrom(
      this.http.post<EventDocumentation>(
        `${this.cfg.host}api/update_documentation/`,
        { documentation: documentation },
        { headers: this.headers }
      )
    );
  }

  /**
   * Upload attachment for event documentation
   * @param documentationId - Documentation ID
   * @param attachmentType - Type of attachment
   * @param fileName - File name
   * @param fileData - Base64 encoded file data
   * @param onProgress - Progress callback
   * @returns Promise with uploaded attachment
   */
  async uploadEventAttachment(
    documentationId: number,
    attachmentType: string,
    fileName: string,
    fileData: string,
    onProgress?: (progress: number) => void
  ): Promise<EventAttachment> {
    const request$ = this.http
      .post<EventAttachment>(
        `${this.cfg.host}api/upload_documentation_attachment/`,
        {
          documentationId,
          attachmentType,
          fileName,
          fileData,
        },
        {
          headers: this.headers,
          reportProgress: true,
          observe: 'events',
        }
      )
      .pipe(
        tap((event: HttpEvent<EventAttachment>) => {
          if (
            event.type === 1 &&
            'total' in event &&
            'loaded' in event &&
            event.total &&
            onProgress
          ) {
            const progress = Math.min(Math.round((100 * event.loaded) / event.total), 98);
            onProgress(progress);
          } else if (event.type === 4 && onProgress) {
            onProgress(100);
          }
        }),
        filter(
          (event: HttpEvent<EventAttachment>): event is HttpResponse<EventAttachment> =>
            event.type === 4
        )
      );

    const event = await firstValueFrom(request$);
    return event.body ?? ({} as EventAttachment);
  }

  /**
   * Delete event documentation attachment
   * @param attachmentId - Attachment ID to delete
   * @returns Promise with success status
   */
  async deleteEventAttachment(attachmentId: number): Promise<boolean> {
    return firstValueFrom(
      this.http.post<boolean>(
        `${this.cfg.host}api/delete_documentation_attachment/`,
        { attachmentId },
        { headers: this.headers }
      )
    );
  }

  // ==================== Upload Progress Tracking ====================

  traceUploadProgress(upload: Observable<any>): Observable<Upload> {
    const initialState: Upload = { state: 'PENDING', progress: 0, event: null };
    const calculateState = (_upload: Upload, event: HttpEvent<unknown>): Upload => {
      if (this.isHttpProgressEvent(event)) {
        return {
          progress: event.total
            ? Math.min(Math.round((100 * event.loaded) / event.total), 98) // leave progress bar unfinished until done event is received
            : _upload.progress,
          state: 'IN_PROGRESS',
          event: event,
        };
      }
      if (this.isHttpResponse(event)) {
        return {
          progress: 100,
          state: 'DONE',
          event: event,
        };
      }
      return _upload;
    };
    return upload.pipe(scan(calculateState, initialState));
  }

  /**
   * Type guard to check if event is an HttpResponse
   */
  private isHttpResponse<T>(event: HttpEvent<T>): event is HttpResponse<T> {
    return event.type === HttpEventType.Response;
  }

  /**
   * Type guard to check if event is an HttpProgressEvent
   */
  private isHttpProgressEvent(event: HttpEvent<unknown>): event is HttpProgressEvent {
    return (
      event.type === HttpEventType.DownloadProgress || event.type === HttpEventType.UploadProgress
    );
  }
}
